/* eslint-disable react/prop-types */
/* eslint-disable no-undef */
import React, { useEffect, useRef, createContext } from 'react'
import { jwtDecode } from 'jwt-decode'
import useAuth from '../hook/useAuth'
import { getToken, systemID } from '../functionTool/functionTool'
import { GetEncryptedString } from '../functionTool/encryption'

export const AuthContext = createContext()

// 提前刷新緩衝：token 到期前這麼久就先刷新（給電視節流一點餘裕）
const REFRESH_BEFORE_MS = 15 * 60 * 1000 // 15 分鐘

export default function ProviderAuth({ children }) {
    const auth = useAuth()
    const authRef = useRef(auth)
    const workerRef = useRef(null)

    // 保持 ref 最新，供事件處理使用
    useEffect(() => {
        authRef.current = auth
    })

    // 監聽 tokenRefreshed（來自 index.js interceptor 或本檔 Worker 的刷新），更新 React context
    useEffect(() => {
        const handleTokenRefreshed = (event) => {
            const current = authRef.current
            if (!current.user) return
            const rawToken = event.detail.token.replace('Bearer ', '').trim()
            current.AuthUser({
                ...current.user,
                token: rawToken, // AuthUser 會自動加 'Bearer '
            })
        }
        window.addEventListener('tokenRefreshed', handleTokenRefreshed)
        return () => {
            window.removeEventListener('tokenRefreshed', handleTokenRefreshed)
        }
    }, [])

    // 建立背景 Worker：定時刷新 token，不受電視/瀏覽器主執行緒計時器節流影響
    useEffect(() => {
        let worker
        try {
            worker = new Worker('/tokenRefreshWorker.js')
        } catch (err) {
            console.error('[AuthProvider] 無法建立 token 刷新 Worker，將僅依賴請求攔截器刷新:', err)
            return undefined
        }
        workerRef.current = worker

        worker.onmessage = (e) => {
            const data = e.data || {}
            if (data.type === 'REFRESHED' && data.token) {
                const bearer = 'Bearer ' + data.token
                // Worker 無法存取 localStorage，由主執行緒寫入並通知 React 更新
                window.localStorage.setItem('token', GetEncryptedString(bearer))
                window.dispatchEvent(
                    new CustomEvent('tokenRefreshed', { detail: { token: bearer } })
                )
            } else if (data.type === 'ERROR') {
                // 刷新失敗不主動登出，交由既有請求攔截器 / 自動重登在下次請求時處理
                console.error('[AuthProvider] Worker token 刷新失敗:', data.error)
            }
        }

        return () => {
            worker.postMessage({ action: 'STOP' })
            worker.terminate()
            workerRef.current = null
        }
    }, [])

    // 每當 token 變化（登入 / 刷新 / 重新整理），重新排程下一次刷新
    useEffect(() => {
        const worker = workerRef.current
        const stored = getToken() // 'Bearer xxx'
        if (!worker || !stored) return
        try {
            const raw = stored.replace('Bearer', '').trim()
            const decoded = jwtDecode(raw)
            const expMs = (decoded?.exp || 0) * 1000
            const delay = Math.max(0, expMs - Date.now() - REFRESH_BEFORE_MS)
            worker.postMessage({
                action: 'INIT_REFRESH',
                refreshTokenUrl: `${process.env.REACT_APP_APIGATEWAY_URL}/auth/refresh`,
                systemID,
                token: stored, // 含 'Bearer '，與 index.js doRefresh 一致
                expiresIn: delay,
            })
        } catch (err) {
            console.error('[AuthProvider] 排程 token 刷新失敗:', err)
        }
    }, [auth.user?.token])

    return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>
}
